/**
 * CDN and Image Optimization Utilities
 * Provides optimized image loading with WebP support and CDN URLs
 */

// CDN base URLs for common resources
export const CDN = {
  FONTS: 'https://fonts.googleapis.com',
  ICONS: 'https://cdn.jsdelivr.net/npm/lucide-static@latest',
  IMAGES: 'https://images.unsplash.com', // For stock photos if needed
};

/**
 * Check if browser supports WebP
 */
export const supportsWebP = () => {
  if (typeof window === 'undefined') return false;
  
  const canvas = document.createElement('canvas');
  if (canvas.getContext && canvas.getContext('2d')) {
    return canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }
  return false;
};

/**
 * Get optimized image URL with format and size parameters
 * Works with Unsplash, Cloudinary, and other image CDNs
 */
export const getOptimizedImageUrl = (url, options = {}) => {
  const { width = 800, quality = 80, format = 'auto' } = options;
  
  if (!url) return '';
  
  // Unsplash optimization
  if (url.includes('unsplash.com')) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}w=${width}&q=${quality}&fm=${format === 'auto' ? 'webp' : format}`;
  }
  
  // Cloudinary optimization
  if (url.includes('cloudinary.com')) {
    return url.replace('/upload/', `/upload/w_${width},q_${quality},f_auto/`);
  }
  
  // Return original if no optimization available
  return url;
};

/**
 * Preload critical images
 */
export const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Preload multiple images in parallel
 */
export const preloadImages = async (urls) => {
  return Promise.allSettled(urls.map(preloadImage));
};

/**
 * Get placeholder blur data URL for images
 */
export const getBlurPlaceholder = (width = 10, height = 10, color = '#374151') => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}">
      <filter id="b" color-interpolation-filters="sRGB">
        <feGaussianBlur stdDeviation="1"/>
      </filter>
      <rect width="100%" height="100%" fill="${color}" filter="url(#b)"/>
    </svg>
  `;
  return `data:image/svg+xml;base64,${btoa(svg.trim())}`;
};

/**
 * Resource hints for faster loading
 * Call this early in app initialization
 */
export const addResourceHints = () => {
  const hints = [
    { rel: 'dns-prefetch', href: 'https://fonts.googleapis.com' },
    { rel: 'dns-prefetch', href: 'https://fonts.gstatic.com' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossOrigin: 'anonymous' },
  ];
  
  hints.forEach(({ rel, href, crossOrigin }) => {
    const existingLink = document.querySelector(`link[rel="${rel}"][href="${href}"]`);
    if (!existingLink) {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      if (crossOrigin) link.crossOrigin = crossOrigin;
      document.head.appendChild(link);
    }
  });
};

export default {
  CDN,
  supportsWebP,
  getOptimizedImageUrl,
  preloadImage,
  preloadImages,
  getBlurPlaceholder,
  addResourceHints,
};
